import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import Theme from 'components/common/Theme/Theme'
import UpdatingBlock from 'components/common/UpdatingBlock'
import Button from 'components/common/Button/Button'
import PromoBlock from './components/PromoBlock'
import SummaryItem from './components/SummaryItem'
import VehicleDetails from './components/VehicleDetails'
import StoreDetails from './components/StoreDetails'
import ApplyCouponForm, { ToggledApplyCouponForm } from './components/ApplyCouponForm'
import EstimatedTotalDisclaimer from 'components/ShoppingQuote/BOTProductAndServicesQuote/components/EstimatedTotalDisclaimer'
import { SHOPPING_QUOTE_TOTAL_QUANTITY } from 'components/common/UpdatingBlock/constants'


const QuoteSummary = ({
  children,
  className,
  currentStore,
  productAndServiceMinusInstantSavings,
  promotions: {
    rebate = [],
    coupons = []
  },
  quoteSummary: {
    displaymessage,
    estimatedTaxes,
    estimatedTotal,
    installationCost,
    instantSavings,
    mailInRebateTotal,
    productAndService,
    shopFees
  } = {},
  showStoreSection = false,
  showVehicleSection = false,
  storeFormattedPhone,
  vehicleDetails: {
    vehicleNickName,
    frontTireSize,
    rearTireSize
  } = {},
  EditLink,
  noEdit,
  goBack,
  services = [],
  isCheckout
}) =>
  <Wrapper data-at-promotions-and-summary className={ className }>
    <UpdatingBlock name={ SHOPPING_QUOTE_TOTAL_QUANTITY } message="Updating Cart">
      {
        showVehicleSection &&
        <VehicleDetails
          vehicleNickName={ vehicleNickName }
          frontSize={ frontTireSize }
          rearSize={ rearTireSize } />
      }
      <Media.Desktop>
        {
          showStoreSection &&
          <StoreDetailsBlock>
            <StoreDetails
              currentStore={ currentStore } />
          </StoreDetailsBlock>
        }
      </Media.Desktop>
      <Block>
        <Headline data-at-qoutesummary-title>
          Initial Quote Summary
          {
            EditLink &&
            <EditLinkContainer link onClick={ () => goBack() }>
              <EditLink />
            </EditLinkContainer>
          }
        </Headline>
        {
          !noEdit &&
          <>
            {
              isCheckout ? <ToggledApplyCouponForm /> : <ApplyCouponForm />
            }
          </>
        }
        {
          Boolean(productAndService) &&
          <SummaryItem title="Products & Services" price={ productAndServiceMinusInstantSavings } />
        }
        {
          Boolean(shopFees) &&
          <SummaryItem title="Shop Fees" price={ shopFees } />
        }
        {
          Boolean(installationCost) &&
          <SummaryItem title="Installation Cost" price={ installationCost } />
        }
        {
          Boolean(estimatedTaxes) &&
          <SummaryItem title="Estimated Taxes" price={ estimatedTaxes } />
        }
        {
          Boolean(instantSavings) &&
          <SummaryItem title="Instant Savings" price={ instantSavings } bolder savingField />
        }
        <Total>
          <SummaryItem
            title="Estimated Total*"
            price={ estimatedTotal }
            displaymessage={ displaymessage }
            phoneNumber={ storeFormattedPhone }
            total />
          {
            Boolean(estimatedTotal && services.length) &&
            <EstimatedTotalDisclaimer services={ services } />
          }
          {
            Boolean(mailInRebateTotal) &&
            <TotalAfterMailRebate
              bold
              smaller
              doubleLine
              price={ mailInRebateTotal }
              dataAttrPrefix="estimated-total-after-rebate"
              title={
                <AfterRebatesTitle>
                  Estimated Total<br />After Mail-In Rebate:
                </AfterRebatesTitle> } />
          }
        </Total>
      </Block>
      { children }
      {
        Boolean(coupons.length) &&
        <PromoBlock atId="applied-promotions" noEdit={ noEdit } name="Applied Promotions" promotions={ coupons } />
      }
      {
        Boolean(rebate.length) &&
        <PromoBlock atId="discounts" noEdit={ noEdit } name="Eligible Rebates" promotions={ rebate } />
      }
    </UpdatingBlock>
  </Wrapper>

QuoteSummary.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  className: PropTypes.string,
  currentStore: PropTypes.shape(),
  EditLink: PropTypes.func,
  goBack: PropTypes.func,
  isCheckout: PropTypes.bool,
  noEdit: PropTypes.bool,
  productAndServiceMinusInstantSavings: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  promotions: PropTypes.shape({
    rebate: PropTypes.arrayOf(PropTypes.shape()),
    coupons: PropTypes.arrayOf(PropTypes.shape())
  }),
  quoteSummary: PropTypes.shape(),
  services: PropTypes.arrayOf(PropTypes.shape()),
  showStoreSection: PropTypes.bool,
  showVehicleSection: PropTypes.bool,
  storeFormattedPhone: PropTypes.string,
  vehicleDetails: PropTypes.shape({
    vehicleName: PropTypes.string,
    frontTireSize: PropTypes.string,
    rearTireSize: PropTypes.string
  })
}

export default QuoteSummary

const Wrapper = styled.div`
  ${Mixin.clearFix()}
  ${Media.mobile`
    padding-top: 20px;
  `}
`

const Block = styled.div`
  background-color: ${Theme.colors.off_white};
  padding: 15px;
`

const StoreDetailsBlock = styled.div`
  margin-bottom: 15px;
  padding: 15px;
  border: 1px solid ${Theme.colors.light_grey};
`

const Headline = styled.div`
  display: flex;
  white-space: nowrap;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 15px;
  ${Mixin.font.GothamBold()}
  line-height: 19px;
  color: ${Theme.colors.sub_grey};
`

const Total = styled.div`
  padding: 12px 0 0;
  margin-top: 10px;
  border-top: 1px solid ${Theme.colors.light_grey};
`

const TotalAfterMailRebate = styled(SummaryItem)`
  margin-top: 5px;
`

const AfterRebatesTitle = styled.span`
  font-size: inherit;
  font-weight: inherit;
`

const EditLinkContainer = styled(Button)`
  && {
    padding: 0;

    & > div {
      margin-bottom: 0;
    }
  }
`
